/**
 * Money Value Object
 * Handles monetary values with proper precision and validation
 */
export class Money {
  private static readonly DECIMAL_PRECISION = 2;
  private static readonly PRECISION_MULTIPLIER = 100;

  private constructor(
    private readonly amountInCents: number,
    private readonly currencyCode: string = 'USD',
  ) {}

  static fromAmount(rawAmount: number, currencyCode: string = 'USD'): Money {
    this.validateAmountIsNotNegative(rawAmount);
    this.validateAmountIsFinite(rawAmount);

    const preciseAmountInCents = this.roundToPrecision(rawAmount);
    const normalizedCurrencyCode = this.normalizeCurrency(currencyCode);

    return new Money(preciseAmountInCents, normalizedCurrencyCode);
  }

  static createZeroAmount(currencyCode: string = 'USD'): Money {
    return new Money(0, currencyCode);
  }

  getAmountInDollars(): number {
    return this.amountInCents / Money.PRECISION_MULTIPLIER;
  }

  getCurrencyCode(): string {
    return this.currencyCode;
  }

  addMoney(otherMoney: Money): Money {
    this.ensureSameCurrency(otherMoney);
    const newAmountInCents = this.amountInCents + otherMoney.amountInCents;
    return new Money(newAmountInCents, this.currencyCode);
  }

  subtractMoney(otherMoney: Money): Money {
    this.ensureSameCurrency(otherMoney);
    const newAmountInCents = this.amountInCents - otherMoney.amountInCents;
    return new Money(newAmountInCents, this.currencyCode);
  }

  multiplyByFactor(multiplicationFactor: number): Money {
    this.validateFactorIsFinite(multiplicationFactor);
    const newAmountInCents = Math.round(this.amountInCents * multiplicationFactor);
    return new Money(newAmountInCents, this.currencyCode);
  }

  isEqualTo(otherMoney: Money): boolean {
    return this.amountInCents === otherMoney.amountInCents && this.currencyCode === otherMoney.currencyCode;
  }

  isGreaterThan(otherMoney: Money): boolean {
    this.ensureSameCurrency(otherMoney);
    return this.amountInCents > otherMoney.amountInCents;
  }

  isZeroAmount(): boolean {
    return this.amountInCents === 0;
  }

  formatForDisplay(): string {
    return `$${this.getAmountInDollars().toFixed(Money.DECIMAL_PRECISION)}`;
  }

  formatWithCurrency(): string {
    return `${this.currencyCode} ${this.getAmountInDollars().toFixed(Money.DECIMAL_PRECISION)}`;
  }

  private static validateAmountIsNotNegative(amount: number): void {
    if (amount < 0) {
      throw new Error('Money amount cannot be negative');
    }
  }

  private static validateAmountIsFinite(amount: number): void {
    if (!Number.isFinite(amount)) {
      throw new Error('Money amount must be a finite number');
    }
  }

  private static roundToPrecision(amount: number): number {
    return Math.round(amount * Money.PRECISION_MULTIPLIER);
  }

  private static normalizeCurrency(currency: string): string {
    return currency.toUpperCase();
  }

  private validateFactorIsFinite(factor: number): void {
    if (!Number.isFinite(factor)) {
      throw new Error('Multiplication factor must be a finite number');
    }
  }

  private ensureSameCurrency(otherMoney: Money): void {
    if (this.currencyCode !== otherMoney.currencyCode) {
      throw new Error(`Currency mismatch: ${this.currencyCode} vs ${otherMoney.currencyCode}`);
    }
  }
}
